/**
 * Unresolved-evidence-conflict notice — ONE source for all four report surfaces
 * (reader HTML, paged/PDF, DOCX, PPTX).
 *
 * WHY THIS EXISTS: `ReportAlertBanner` warned about high-severity unresolved
 * evidence conflicts in the READER only. Nothing in `reportDocxExport`,
 * `reportPptxExport` or the PDF path referenced them, so a report whose
 * conflicts are severe enough for `evaluateSiteDataAdequacy` to withhold the
 * ENTIRE site verdict could be exported and circulated with no warning at all.
 * The warning lived where the reader could already see the site's state, and was
 * absent from the artifact that leaves the building.
 *
 * DELIBERATELY ADDITIVE. This is a SEPARATE conditional block, not a new entry in
 * `REPORT_LEGAL_DISCLAIMER_PARAGRAPHS`: those fixed paragraphs are asserted
 * verbatim by `validatePdfArtifact` and pinned by the docx/pptx smokes and the
 * visual-matrix baselines. A report with no unresolved high-severity conflicts
 * produces `null` here and renders exactly as before, so every existing baseline
 * and validator stays byte-stable. Only an affected report gains the notice.
 *
 * FROZEN, NOT LIVE. The count is read from `report.unresolvedConflicts`, stamped
 * onto the report at generation time — not recomputed from the current SED. An
 * exported artifact must state what was true when it was generated; the reader
 * separately shows the live drift (`computeLiveUnresolvedConflicts`) because it
 * can act on it.
 */
import type { SiteRagReport } from '../types/siteRag';

export const REPORT_CONFLICT_NOTICE_HEADING = 'Unresolved Evidence Conflicts';

export interface ReportConflictNotice {
  heading: string;
  /** One paragraph, safe to emit verbatim into any surface. */
  body: string;
  /** High-severity count the notice is reporting. Always > 0. */
  highCount: number;
}

/**
 * Resolve the notice for a report, or `null` when there is nothing to warn about.
 *
 * Only HIGH severity qualifies: low/medium conflicts are surfaced in the quality
 * panel but do not move the deterministic verdict, so warning on them in a
 * circulated artifact would cry wolf. This mirrors the gate in
 * `evaluateSiteDataAdequacy`, which also keys on high-severity conflicts alone.
 */
export function resolveReportConflictNotice(
  report: Pick<SiteRagReport, 'unresolvedConflicts'>,
): ReportConflictNotice | null {
  const high = report.unresolvedConflicts?.bySeverity?.high ?? 0;
  if (!Number.isFinite(high) || high <= 0) return null;
  const plural = high === 1 ? '' : 's';
  const wasWere = high === 1 ? 'was' : 'were';
  return {
    heading: REPORT_CONFLICT_NOTICE_HEADING,
    body:
      `${high} high-severity evidence conflict${plural} ${wasWere} unresolved when this report was `
      + 'generated. A high-severity conflict means two sources disagree materially about the same '
      + 'field and the disagreement had not been reconciled. Figures derived from the affected '
      + 'fields may be wrong, and this is severe enough that the platform withholds the site\'s '
      + 'Go/No-Go verdict. Reconcile the conflicts in Site Evidence and re-run this analysis '
      + 'before relying on it for a decision.',
    highCount: high,
  };
}
